import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";

import dagre from "dagre";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
} from "d3-force";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";

import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Menu,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "./auth";
import {
  VISITOR_STORAGE_KEYS,
  clearVisitorData,
  loadVisitorData,
  saveVisitorData,
} from "./visitorStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/* =========================================================
   Graph API Helpers
========================================================= */
function safeParseJSON(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

async function listProjects() {
  const res = await fetch(`${API_BASE}/api/graphs`);
  if (!res.ok) throw new Error("Failed to load graphs");
  return res.json();
}

async function loadProject(id) {
  const res = await fetch(`${API_BASE}/api/graphs/${id}`);
  if (!res.ok) throw new Error("Failed to load graph");
  return res.json();
}

async function createProject(payload) {
  const res = await fetch(`${API_BASE}/api/graphs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) throw new Error("DUPLICATE_NAME");
  if (!res.ok) throw new Error("Failed to create graph");
  return res.json();
}

async function saveProject(id, payload) {
  const res = await fetch(`${API_BASE}/api/graphs/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) throw new Error("DUPLICATE_NAME");
  if (!res.ok) throw new Error("Failed to update graph");
  return res.json();
}

async function deleteProject(id) {
  const res = await fetch(`${API_BASE}/api/graphs/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete graph");
}

const VISITOR_PROJECTS_KEY = VISITOR_STORAGE_KEYS.transactionProjects;

const loadVisitorProjects = () =>
  loadVisitorData(VISITOR_PROJECTS_KEY, []);

const saveVisitorProjects = (projects) => {
  saveVisitorData(VISITOR_PROJECTS_KEY, projects);
};

async function listProjectsWithMode(visitorMode) {
  if (!visitorMode) return listProjects();
  const projects = loadVisitorProjects();
  return projects
    .slice()
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")))
    .map((p) => ({
      id: p.id,
      name: p.name,
      updated_at: p.updatedAt,
    }));
}

async function loadProjectWithMode(id, visitorMode) {
  if (!visitorMode) return loadProject(id);
  const projects = loadVisitorProjects();
  const project = projects.find((p) => String(p.id) === String(id));
  if (!project) throw new Error("Failed to load graph");
  return {
    id: project.id,
    name: project.name,
    nodes: project.nodes || [],
    edges: project.edges || [],
    layoutDir: project.layoutDir || "LR",
  };
}

async function createProjectWithMode(payload, visitorMode) {
  if (!visitorMode) return createProject(payload);
  const projects = loadVisitorProjects();
  const incomingName = String(payload?.name || "").trim();
  if (!incomingName) throw new Error("Failed to create graph");

  const duplicate = projects.find(
    (p) => String(p.name || "").trim().toLowerCase() === incomingName.toLowerCase()
  );
  if (duplicate) throw new Error("DUPLICATE_NAME");

  const now = new Date().toISOString();
  const project = {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: incomingName,
    nodes: Array.isArray(payload?.nodes) ? payload.nodes : [],
    edges: Array.isArray(payload?.edges) ? payload.edges : [],
    layoutDir: payload?.layoutDir || "LR",
    updatedAt: now,
  };
  saveVisitorProjects([project, ...projects]);
  return {
    id: project.id,
    name: project.name,
    nodes: project.nodes,
    edges: project.edges,
    layoutDir: project.layoutDir,
  };
}

async function saveProjectWithMode(id, payload, visitorMode) {
  if (!visitorMode) return saveProject(id, payload);
  const projects = loadVisitorProjects();
  const index = projects.findIndex((p) => String(p.id) === String(id));
  if (index < 0) throw new Error("Failed to update graph");

  const incomingName = String(payload?.name || projects[index].name || "").trim();
  const duplicate = projects.find(
    (p, i) =>
      i !== index &&
      String(p.name || "").trim().toLowerCase() === incomingName.toLowerCase()
  );
  if (duplicate) throw new Error("DUPLICATE_NAME");

  const updated = {
    ...projects[index],
    name: incomingName || projects[index].name,
    nodes: Array.isArray(payload?.nodes) ? payload.nodes : projects[index].nodes,
    edges: Array.isArray(payload?.edges) ? payload.edges : projects[index].edges,
    layoutDir: payload?.layoutDir || projects[index].layoutDir || "LR",
    updatedAt: new Date().toISOString(),
  };
  const next = projects.slice();
  next[index] = updated;
  saveVisitorProjects(next);
  return {
    id: updated.id,
    name: updated.name,
    nodes: updated.nodes,
    edges: updated.edges,
    layoutDir: updated.layoutDir,
  };
}

async function deleteProjectWithMode(id, visitorMode) {
  if (!visitorMode) return deleteProject(id);
  const projects = loadVisitorProjects();
  const next = projects.filter((p) => String(p.id) !== String(id));
  saveVisitorProjects(next);
}

/* =========================================================
   Helpers
========================================================= */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const safeNum = (v) => {
  const n = Number(String(v ?? "").replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : 0;
};

const getBankColor = (bank) => {
  const map = {
    "Meezan Bank": "#16a34a",
    "Faysal Bank": "#1f4ed8",
    "UBL": "#dc2626",
    "HBL": "#7c3aed",
    "MCB": "#0ea5e9",
    "Alfalah": "#f59e0b",
    Default: "#6b7280",
  };
  return map[bank] || map.Default;
};

const computeEdgeColor = (amount) => {
  // per-edge color change feature (node→child)
  if (amount >= 500000) return "#dc2626"; // red
  if (amount >= 200000) return "#f97316"; // orange
  if (amount > 0) return "#2563eb"; // blue
  return "#0f172a"; // slate
};

const computeEdgeWidth = (amount) => {
  // clear visible line thickness by amount
  const w = amount / 80000;
  return clamp(w, 2.2, 7);
};

const buildFriendlyNodeLabel = (index) => `TX-${String(index + 1).padStart(2, "0")}`;
const EDGE_SETTINGS_STORAGE_KEY = "transactionTree.edgeSettings.v1";
const EDGE_TYPE_OPTIONS = ["auto", "bezier", "smoothstep", "step", "straight"];
const DEFAULT_EDGE_SETTINGS = {
  type: "auto",
  lineWidth: 3,
  headSize: 18,
};
const ROOT_TREE_GAP_X = 320;
const ROOT_TREE_GAP_Y = 240;

const DEFAULT_TRANSACTION_FORM = {
  name: "",
  account: "",
  bank: "",
  amount: "",
  date: "",
  time: "",
  channel: "IBFT",
  instrument: "Bank",
  agentShop: "",
  agentCity: "",
  shape: "rounded",
};

const formFromNode = (node) => {
  const data = node?.data || {};
  return {
    ...DEFAULT_TRANSACTION_FORM,
    name: data.name || "",
    account: data.account || "",
    bank: data.bank || "",
    amount: data.amount ?? "",
    date: data.date || "",
    time: data.time || "",
    channel: data.channel || "IBFT",
    instrument: data.instrument || "Bank",
    agentShop: data.agentShop || "",
    agentCity: data.agentCity || "",
    shape: data.shape || "rounded",
  };
};

const sanitizeEdgeSettings = (incoming) => {
  const raw = incoming && typeof incoming === "object" ? incoming : {};
  const type = EDGE_TYPE_OPTIONS.includes(raw.type) ? raw.type : DEFAULT_EDGE_SETTINGS.type;
  const lineWidth = clamp(Number(raw.lineWidth) || DEFAULT_EDGE_SETTINGS.lineWidth, 1, 12);
  const headSize = clamp(Number(raw.headSize) || DEFAULT_EDGE_SETTINGS.headSize, 10, 40);
  return {
    type,
    lineWidth,
    headSize,
  };
};

const loadStoredEdgeSettings = () => {
  if (typeof window === "undefined") return DEFAULT_EDGE_SETTINGS;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(EDGE_SETTINGS_STORAGE_KEY) || "{}");
    return sanitizeEdgeSettings(parsed);
  } catch {
    return DEFAULT_EDGE_SETTINGS;
  }
};

const saveStoredEdgeSettings = (settings) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    EDGE_SETTINGS_STORAGE_KEY,
    JSON.stringify(sanitizeEdgeSettings(settings))
  );
};

const getEdgeLayoutProps = (layoutDir, heavy, preferredType = "auto") => {
  const vertical = layoutDir === "TB";
  const resolvedType =
    preferredType && preferredType !== "auto"
      ? preferredType
      : vertical
        ? "smoothstep"
        : "bezier";
  const animated = !heavy && !vertical && resolvedType === "bezier" && preferredType === "auto";
  return {
    type: resolvedType,
    animated,
    sourcePosition: vertical ? Position.Bottom : Position.Right,
    targetPosition: vertical ? Position.Top : Position.Left,
    ...((resolvedType === "smoothstep" || resolvedType === "step") && vertical
      ? { pathOptions: { offset: 18, borderRadius: 14 } }
      : {}),
  };
};

const normalizeEdgeForLayout = (
  edge,
  layoutDir,
  heavy,
  edgeSettings = DEFAULT_EDGE_SETTINGS,
  applySettings = false
) => {
  const settings = sanitizeEdgeSettings(edgeSettings);
  const amount = safeNum(edge?.data?.amount);
  const color =
    edge?.data?.color ||
    edge?.markerEnd?.color ||
    edge?.style?.stroke ||
    computeEdgeColor(amount);
  const fallbackWidth = Number(edge?.style?.strokeWidth) || computeEdgeWidth(amount);
  const strokeWidth = applySettings
    ? settings.lineWidth
    : clamp(
        Number(edge?.data?.lineWidth) || fallbackWidth || settings.lineWidth,
        1,
        12
      );
  const headSize = applySettings
    ? settings.headSize
    : clamp(
        Number(edge?.data?.headSize) ||
          Number(edge?.markerEnd?.width) ||
          settings.headSize,
        10,
        40
      );
  const preferredType = applySettings
    ? settings.type
    : edge?.data?.edgeType || settings.type;

  return {
    ...edge,
    ...getEdgeLayoutProps(layoutDir, heavy, preferredType),
    style: {
      ...(edge.style || {}),
      stroke: color,
      strokeWidth,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color,
      width: headSize,
      height: headSize,
    },
    data: {
      ...(edge.data || {}),
      amount,
      color,
      lineWidth: strokeWidth,
      headSize,
      edgeType: preferredType,
    },
  };
};

const normalizeEdgesForLayout = (
  edges,
  layoutDir,
  heavy,
  edgeSettings = DEFAULT_EDGE_SETTINGS,
  applySettings = false
) =>
  (Array.isArray(edges) ? edges : []).map((edge) =>
    normalizeEdgeForLayout(edge, layoutDir, heavy, edgeSettings, applySettings)
  );

const buildTransactionEdge = ({
  id,
  source,
  target,
  amount,
  layoutDir,
  heavy,
  edgeSettings,
}) => {
  const color = computeEdgeColor(amount);
  return normalizeEdgeForLayout(
    {
      id,
      source,
      target,
      style: {
        stroke: color,
        strokeWidth: computeEdgeWidth(amount),
      },
      data: { amount, color },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color,
      },
    },
    layoutDir,
    heavy,
    edgeSettings,
    true
  );
};

const layoutWithDagre = (nodes, edges, direction /* LR or TB */) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: direction,
    nodesep: 55,
    ranksep: 95,
  });

  nodes.forEach((n) => {
    const w = Number(n?.style?.width ?? 230);
    const h = Number(n?.style?.height ?? 140);
    g.setNode(n.id, { width: w, height: h });
  });

  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    const w = Number(n?.style?.width ?? 230);
    const h = Number(n?.style?.height ?? 140);
    return {
      ...n,
      position: { x: pos.x - w / 2, y: pos.y - h / 2 },
    };
  });
};

const getRootNodes = (nodes, edges) => {
  const targets = new Set((Array.isArray(edges) ? edges : []).map((e) => e.target));
  return (Array.isArray(nodes) ? nodes : []).filter((n) => !targets.has(n.id));
};

const getRootNodePosition = (rootIndex, layoutDir) =>
  layoutDir === "LR"
    ? { x: 0, y: rootIndex * ROOT_TREE_GAP_Y }
    : { x: rootIndex * ROOT_TREE_GAP_X, y: 0 };

const getChildNodePosition = (parentNode, edges, layoutDir) => {
  const childCount = (Array.isArray(edges) ? edges : []).filter(
    (edge) => edge.source === parentNode.id
  ).length;
  if (layoutDir === "LR") {
    return {
      x: parentNode.position.x + 280,
      y: parentNode.position.y + childCount * 170,
    };
  }
  return {
    x: parentNode.position.x + childCount * 250,
    y: parentNode.position.y + 220,
  };
};

const layoutAllTrees = (nodes, edges, direction /* LR or TB */) => {
  const sourceNodes = Array.isArray(nodes) ? nodes : [];
  const sourceEdges = Array.isArray(edges) ? edges : [];
  if (!sourceNodes.length) return [];

  const nodeById = new Map(sourceNodes.map((node) => [node.id, node]));
  const childrenMap = new Map();
  sourceEdges.forEach((edge) => {
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) return;
    if (!childrenMap.has(edge.source)) {
      childrenMap.set(edge.source, []);
    }
    childrenMap.get(edge.source).push(edge.target);
  });

  const visited = new Set();
  const components = [];

  const collectComponent = (startId) => {
    if (visited.has(startId)) return;
    const queue = [startId];
    const ids = [];
    visited.add(startId);
    while (queue.length) {
      const currentId = queue.shift();
      ids.push(currentId);
      const children = childrenMap.get(currentId) || [];
      children.forEach((childId) => {
        if (visited.has(childId)) return;
        visited.add(childId);
        queue.push(childId);
      });
    }
    if (ids.length) {
      components.push(ids);
    }
  };

  // Place each root-tree in its own lane/column to prevent overlap.
  getRootNodes(sourceNodes, sourceEdges).forEach((rootNode) => collectComponent(rootNode.id));
  sourceNodes.forEach((node) => collectComponent(node.id));

  let offsetX = 0;
  let offsetY = 0;
  const laidOutById = new Map();

  components.forEach((componentIds) => {
    const idSet = new Set(componentIds);
    const componentNodes = componentIds.map((id) => nodeById.get(id)).filter(Boolean);
    const componentEdges = sourceEdges.filter(
      (edge) => idSet.has(edge.source) && idSet.has(edge.target)
    );
    const arranged = layoutWithDagre(componentNodes, componentEdges, direction);

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    arranged.forEach((node) => {
      const width = Number(node?.style?.width ?? 230);
      const height = Number(node?.style?.height ?? 140);
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
      maxY = Math.max(maxY, node.position.y + height);
    });

    const normalizeX = offsetX - minX;
    const normalizeY = offsetY - minY;
    arranged.forEach((node) => {
      laidOutById.set(node.id, {
        ...node,
        position: {
          x: node.position.x + normalizeX,
          y: node.position.y + normalizeY,
        },
      });
    });

    const componentWidth = Math.max(0, maxX - minX);
    const componentHeight = Math.max(0, maxY - minY);
    if (direction === "LR") {
      offsetY += componentHeight + ROOT_TREE_GAP_Y;
    } else {
      offsetX += componentWidth + ROOT_TREE_GAP_X;
    }
  });

  return sourceNodes.map((node) => laidOutById.get(node.id) || node);
};

/* =========================================================
   Custom Node (visual tags for channel/instrument/agent)
========================================================= */
function Tag({ label }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        padding: "2px 8px",
        borderRadius: 999,
        background: "#f1f5f9",
        color: "#0f172a",
        border: "1px solid #e2e8f0",
        maxWidth: 240,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={label}
    >
      {label}
    </span>
  );
}

function TransactionNode({ data, selected }) {
  const {
    name,
    account,
    bank,
    amount,
    date,
    time,
    channel,
    instrument,
    agentShop,
    agentCity,
    bankColor,
    layoutDir,
    readOnly,
    shape,
    nodeLabel,
  } = data;

  const sourcePos = layoutDir === "LR" ? Position.Right : Position.Bottom;
  const targetPos = layoutDir === "LR" ? Position.Left : Position.Top;

  const showAgent = channel === "Cash-out agent";
  const shapeStyle = (() => {
    switch (shape) {
      case "pill":
        return { borderRadius: 999 };
      case "circle":
        return { borderRadius: "50%" };
      case "sharp":
        return { borderRadius: 6 };
      default:
        return { borderRadius: 16 };
    }
  })();

  return (
    <div
      style={{
        ...shapeStyle,
        border: `3px solid ${bankColor}`,
        background: "#fff",
        width: "100%",
        height: "100%",
        padding: 10,
        fontSize: 12,
        position: "relative",
        boxShadow: selected
          ? "0 0 0 3px rgba(37,99,235,0.22)"
          : "0 1px 2px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Stretch nodes */}
      {!readOnly && (
        <NodeResizer
          isVisible={selected}
          minWidth={200}
          minHeight={120}
          lineStyle={{ borderColor: "rgba(15,23,42,0.35)" }}
          handleStyle={{ width: 10, height: 10 }}
        />
      )}

      {/* Edge-to-edge connections */}
      <Handle type="target" position={targetPos} style={{ opacity: 0 }} />
      <Handle type="source" position={sourcePos} style={{ opacity: 0 }} />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: bankColor,
            marginTop: 4,
            flex: "0 0 auto",
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <div
              style={{
                fontWeight: 900,
                fontSize: 13,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name || "—"}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>
              {nodeLabel || "TX"}
            </div>
          </div>

          <div style={{ color: "#334155" }}>{bank || "—"}</div>
          <div style={{ color: "#334155" }}>Ac: {account || "—"}</div>
          <div style={{ color: "#334155" }}>Rs. {amount || "—"}</div>
          <div style={{ color: "#475569" }}>
            {date || "—"} {time || "—"}
          </div>

          <div style={{ marginTop: 7, display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Tag label={`Channel: ${channel || "—"}`} />
            <Tag label={`Instrument: ${instrument || "—"}`} />
            {showAgent && (
              <>
                <Tag label={`Shop: ${agentShop || "—"}`} />
                <Tag label={`City: ${agentCity || "—"}`} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   Main Component
========================================================= */
export default function FinancialTransactionTrail({ visitorMode = false }) {
  const flowWrapRef = useRef(null);
  const reactFlowInstanceRef = useRef(null);
  const edgesRef = useRef([]);
  const lastDragPosRef = useRef({});
  const nodesRef = useRef([]);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const restoringRef = useRef(false);
  const simulationRef = useRef(null);
  const { canEdit, canDelete } = useAuth();
  const effectiveCanEdit = visitorMode ? true : canEdit;
  const effectiveCanDelete = visitorMode ? true : canDelete;

  // loader appears initially
  const [showLoader, setShowLoader] = useState(true);

  const [projectList, setProjectList] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");

  const [layoutDir, setLayoutDir] = useState("LR"); // LR or TB
  const [autoArrangeOnAdd, setAutoArrangeOnAdd] = useState(true);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const physicsEnabled = false;
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [historyLength, setHistoryLength] = useState(0);
  const [edgeSettings, setEdgeSettings] = useState(() => loadStoredEdgeSettings());

  const readOnly = !effectiveCanEdit;

  const heavy = nodes.length > 400;

  // Form with date/time and new fields
  const [form, setForm] = useState(DEFAULT_TRANSACTION_FORM);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT_TRANSACTION_FORM);
  const [addToast, setAddToast] = useState({ open: false, message: "" });

  const nodeTypes = useMemo(() => ({ transaction: TransactionNode }), []);

  const syncGlobals = useCallback(
    (ns, dir = layoutDir) =>
      ns.map((n, index) => ({
        ...n,
        style: {
          ...(n.style || {}),
          transition: "transform 140ms ease-out",
        },
        data: {
          ...n.data,
          nodeLabel: buildFriendlyNodeLabel(index),
          layoutDir: dir,
          readOnly,
        },
      })),
    [layoutDir, readOnly]
  );

  const applyLayout = useCallback(
    (ns, es) => layoutAllTrees(syncGlobals(ns), es, layoutDir),
    [layoutDir, syncGlobals]
  );

  const fitToView = useCallback(() => {
    requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView({ padding: 0.2, duration: 300 });
    });
  }, []);

  const resetAddForm = useCallback(() => {
    setForm(DEFAULT_TRANSACTION_FORM);
  }, []);

  const showAddToast = useCallback((message) => {
    setAddToast({ open: true, message });
  }, []);

  const closeAddToast = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setAddToast((prev) => ({ ...prev, open: false }));
  }, []);

  const cloneGraph = (ns, es) => ({
    nodes: ns.map((n) => ({
      ...n,
      position: { ...n.position },
      data: { ...(n.data || {}) },
      style: { ...(n.style || {}) },
    })),
    edges: es.map((e) => ({
      ...e,
      data: { ...(e.data || {}) },
      style: { ...(e.style || {}) },
    })),
  });

  const initHistory = useCallback((ns, es) => {
    const snapshot = cloneGraph(ns, es);
    historyRef.current = [snapshot];
    historyIndexRef.current = 0;
    setHistoryIndex(0);
    setHistoryLength(1);
  }, []);

  const pushHistory = useCallback((ns, es) => {
    if (restoringRef.current) return;
    const snapshot = cloneGraph(ns, es);
    const next = historyRef.current.slice(0, historyIndexRef.current + 1);
    next.push(snapshot);
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
    setHistoryIndex(historyIndexRef.current);
    setHistoryLength(next.length);
  }, []);

  const startPhysics = useCallback((ns, es) => {
    if (!ns.length) return;
    simulationRef.current?.stop();
    const rect = flowWrapRef.current?.getBoundingClientRect();
    const width = rect?.width || 900;
    const height = rect?.height || 600;
    const simNodes = ns.map((n) => ({
      id: n.id,
      x: n.position.x,
      y: n.position.y,
      fx: n.fx ?? null,
      fy: n.fy ?? null,
    }));
    const idToNode = new Map(simNodes.map((n) => [n.id, n]));
    const links = es
      .map((e) => ({ source: idToNode.get(e.source), target: idToNode.get(e.target) }))
      .filter((l) => l.source && l.target);

    const sim = forceSimulation(simNodes)
      .force("charge", forceManyBody().strength(-420))
      .force("link", forceLink(links).id((d) => d.id).distance(180))
      .force("collide", forceCollide(90))
      .force("center", forceCenter(width / 2, height / 2))
      .alpha(1)
      .alphaDecay(0.03);

    sim.on("tick", () => {
      setNodes((prev) =>
        prev.map((n) => {
          const sn = idToNode.get(n.id);
          if (!sn) return n;
          return { ...n, position: { x: sn.x, y: sn.y } };
        })
      );
    });
    simulationRef.current = sim;
  }, []);


  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    restoringRef.current = true;
    const nextIndex = historyIndexRef.current - 1;
    const snapshot = historyRef.current[nextIndex];
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setSelectedNode(null);
    if (physicsEnabled) {
      startPhysics(snapshot.nodes, snapshot.edges);
    }
    restoringRef.current = false;
  }, [physicsEnabled, startPhysics]);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    restoringRef.current = true;
    const nextIndex = historyIndexRef.current + 1;
    const snapshot = historyRef.current[nextIndex];
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setSelectedNode(null);
    if (physicsEnabled) {
      startPhysics(snapshot.nodes, snapshot.edges);
    }
    restoringRef.current = false;
  }, [physicsEnabled, startPhysics]);

  const handleEdgeSettingChange = useCallback((key, value) => {
    setEdgeSettings((prev) => sanitizeEdgeSettings({ ...prev, [key]: value }));
  }, []);

  const handleForm = useCallback(
    (e) => {
      const { name, value } = e.target;
      setForm((p) => ({ ...p, [name]: value }));

      // Allow direct shape editing on currently selected node.
      if (name !== "shape" || !selectedNode) return;

      const nextNodes = nodesRef.current.map((node) =>
        node.id === selectedNode.id
          ? {
              ...node,
              data: {
                ...(node.data || {}),
                shape: value,
              },
            }
          : node
      );

      const updatedSelectedNode = nextNodes.find((node) => node.id === selectedNode.id) || null;
      setNodes(nextNodes);
      setSelectedNode(updatedSelectedNode);
      pushHistory(nextNodes, edgesRef.current);
    },
    [pushHistory, selectedNode]
  );

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const normalizeGraph = useCallback((graph) => {
    const nodes = Array.isArray(graph?.nodes) ? graph.nodes : safeParseJSON(graph?.nodes, []);
    const edges = Array.isArray(graph?.edges) ? graph.edges : safeParseJSON(graph?.edges, []);
    const dir = graph?.layout_dir || graph?.layoutDir || "LR";
    const normalizedEdges = normalizeEdgesForLayout(
      edges,
      dir,
      nodes.length > 400,
      edgeSettings,
      true
    );
    return { nodes, edges: normalizedEdges, layoutDir: dir };
  }, [edgeSettings]);

  /* ---------------- Project Actions ---------------- */

  const refreshProjectList = useCallback(async () => {
    try {
      const list = await listProjectsWithMode(visitorMode);
      setProjectList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load projects:", error);
      setProjectList([]);
    }
  }, [visitorMode]);

  useEffect(() => {
    refreshProjectList();
  }, [refreshProjectList]);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  useEffect(() => {
    if (!selectedNode) return;
    const selectedShape = selectedNode?.data?.shape || "rounded";
    setForm((prev) => (prev.shape === selectedShape ? prev : { ...prev, shape: selectedShape }));
  }, [selectedNode]);

  useEffect(() => {
    saveStoredEdgeSettings(edgeSettings);
  }, [edgeSettings]);

  useEffect(() => {
    const nextEdges = normalizeEdgesForLayout(
      edgesRef.current,
      layoutDir,
      heavy,
      edgeSettings,
      true
    );
    setEdges(nextEdges);
  }, [edgeSettings, heavy, layoutDir]);

  const startNew = useCallback(async () => {
    if (!effectiveCanEdit) {
      alert("Permission denied");
      return;
    }
    const name = projectName.trim();
    if (!name) return alert("Enter a project name first.");

    try {
      const created = await createProjectWithMode({
        name,
        layoutDir,
        nodes: [],
        edges: [],
      }, visitorMode);
      setProjectId(created.id);
      setProjectName(created.name || name);
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      initHistory([], []);
      await refreshProjectList();
      setShowLoader(false);
    } catch (error) {
      if (String(error?.message) === "DUPLICATE_NAME") {
        alert("A project with this name already exists.");
        return;
      }
      console.error("Failed to create project:", error);
      alert("Failed to create project.");
    }
  }, [effectiveCanEdit, layoutDir, projectName, refreshProjectList, initHistory, visitorMode]);

  const resumeLast = useCallback(async () => {
    try {
      const list = await listProjectsWithMode(visitorMode);
      if (!list.length) return alert("No last project found.");
      const last = list[0];
      const proj = await loadProjectWithMode(last.id, visitorMode);
      if (!proj) return alert("Last project not available.");

      const normalized = normalizeGraph(proj);
      setLayoutDir(normalized.layoutDir || "LR");
      setProjectId(proj.id);
      setProjectName(proj.name || last.name || "");
      setNodes(syncGlobals(normalized.nodes, normalized.layoutDir));
      setEdges(normalized.edges);
      setSelectedNode(null);
      setShowLoader(false);
      initHistory(normalized.nodes, normalized.edges);
      if (normalized.nodes?.length) {
        fitToView();
      }
    } catch (error) {
      console.error("Failed to resume project:", error);
      alert("Failed to resume last project.");
    }
  }, [normalizeGraph, syncGlobals, initHistory, visitorMode]);

  const openProject = useCallback(
    async (project) => {
      const id = typeof project === "object" ? project.id : project;
      if (!id) return;
      try {
        const proj = await loadProjectWithMode(id, visitorMode);
        if (!proj) return;

        const normalized = normalizeGraph(proj);
        setLayoutDir(normalized.layoutDir || "LR");
        setProjectId(proj.id);
        setProjectName(proj.name || (typeof project === "object" ? project.name : "") || "");
        setNodes(syncGlobals(normalized.nodes, normalized.layoutDir));
        setEdges(normalized.edges);
        setSelectedNode(null);
        setShowLoader(false);
        initHistory(normalized.nodes, normalized.edges);
        if (normalized.nodes?.length) {
          fitToView();
        }
      } catch (error) {
        console.error("Failed to open project:", error);
        alert("Failed to open project.");
      }
    },
    [normalizeGraph, syncGlobals, initHistory, visitorMode]
  );

  const removeProject = useCallback(
    async (project) => {
      if (!effectiveCanDelete) {
        alert("Permission denied");
        return;
      }
      const id = typeof project === "object" ? project.id : project;
      if (!id) return;
      try {
        await deleteProjectWithMode(id, visitorMode);
        if (projectId === id) {
          setProjectId(null);
          setProjectName("");
          setNodes([]);
          setEdges([]);
          setSelectedNode(null);
          setShowLoader(true);
        }
        await refreshProjectList();
      } catch (error) {
        console.error("Failed to delete project:", error);
        alert("Failed to delete project.");
      }
    },
    [effectiveCanDelete, projectId, refreshProjectList, visitorMode]
  );

  const importJSON = useCallback(
    (file) => {
      if (!effectiveCanEdit) {
        alert("Permission denied");
        return;
      }
      file.text().then(async (txt) => {
        const parsed = safeParseJSON(txt, {});
        const loadedNodes = Array.isArray(parsed.nodes) ? parsed.nodes : [];
        const loadedEdges = Array.isArray(parsed.edges) ? parsed.edges : [];
        const nameFromFile =
          parsed?.meta?.projectName || file.name.replace(/\.json$/i, "");
        const dirFromFile = parsed?.meta?.layoutDir || "LR";
        const normalized = normalizeGraph({
          nodes: loadedNodes,
          edges: loadedEdges,
          layoutDir: dirFromFile,
        });

        setLayoutDir(normalized.layoutDir || "LR");
        setProjectName(nameFromFile);
        setNodes(syncGlobals(normalized.nodes, normalized.layoutDir));
        setEdges(normalized.edges);
        setSelectedNode(null);

        try {
          const existing = projectList.find((p) => p.name === nameFromFile);
          const saved = existing
            ? await saveProjectWithMode(existing.id, {
                name: nameFromFile,
                layoutDir: normalized.layoutDir,
                nodes: normalized.nodes,
                edges: normalized.edges,
              }, visitorMode)
            : await createProjectWithMode({
                name: nameFromFile,
                layoutDir: normalized.layoutDir,
                nodes: normalized.nodes,
                edges: normalized.edges,
              }, visitorMode);
          setProjectId(saved.id);
          await refreshProjectList();
          setShowLoader(false);
          initHistory(normalized.nodes, normalized.edges);
          if (normalized.nodes.length) {
            fitToView();
          }
        } catch (error) {
          console.error("Failed to import project:", error);
          alert("Failed to import project.");
        }
      });
    },
    [effectiveCanEdit, projectList, refreshProjectList, syncGlobals, initHistory, normalizeGraph, visitorMode]
  );

  /* ---------------- Auto-save ---------------- */
  useEffect(() => {
    if (!effectiveCanEdit) return;
    if (showLoader) return;
    if (!projectId) return;

    const t = setTimeout(async () => {
      try {
        await saveProjectWithMode(projectId, { name: projectName, nodes, edges, layoutDir }, visitorMode);
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }, 600);

    return () => clearTimeout(t);
  }, [effectiveCanEdit, edges, layoutDir, nodes, projectId, projectName, showLoader, visitorMode]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // keep globals synced when layoutDir changes
  useEffect(() => {
    setNodes((prev) => syncGlobals(prev));
  }, [layoutDir, syncGlobals]);

  /* ---------------- Graph Actions ---------------- */

  const addRoot = useCallback(() => {
    const id = `tx-${Date.now()}`;
    const bankColor = getBankColor(form.bank);
    const amount = safeNum(form.amount);
    const edgeSafeAmount = amount ? String(amount) : "";
    const rootPosition =
      autoArrangeOnAdd && !heavy
        ? { x: 0, y: 0 }
        : getRootNodePosition(getRootNodes(nodes, edges).length, layoutDir);

    const newNode = {
      id,
      type: "transaction",
      position: rootPosition,
      style: { width: 230, height: 140, transition: "transform 140ms ease-out" },
      data: {
        ...form,
        amount: edgeSafeAmount,
        bankColor,
        layoutDir,
        readOnly,
        shape: form.shape || "rounded",
      },
    };

    const ns = [...nodes, newNode];
    const finalNodes =
      autoArrangeOnAdd && !heavy ? applyLayout(ns, edges) : syncGlobals(ns);
    setNodes(finalNodes);
    fitToView();
    pushHistory(finalNodes, edges);
    if (physicsEnabled) {
      startPhysics(finalNodes, edges);
    }
    resetAddForm();
    showAddToast("Root added");
  }, [
    applyLayout,
    autoArrangeOnAdd,
    edges,
    form,
    heavy,
    layoutDir,
    nodes,
    resetAddForm,
    pushHistory,
    physicsEnabled,
    showAddToast,
    startPhysics,
    syncGlobals,
  ]);

  const addChild = useCallback(() => {
    if (!selectedNode) return alert("Select a parent node first.");

    const id = `tx-${Date.now()}`;
    const bankColor = getBankColor(form.bank);
    const amount = safeNum(form.amount);

    const newNode = {
      id,
      type: "transaction",
      position:
        autoArrangeOnAdd && !heavy
          ? { x: 0, y: 0 }
          : getChildNodePosition(selectedNode, edges, layoutDir),
      style: { width: 230, height: 140, transition: "transform 140ms ease-out" },
      data: {
        ...form,
        amount: amount ? String(amount) : "",
        bankColor,
        layoutDir,
        readOnly,
        shape: form.shape || "rounded",
      },
    };

    const newEdge = buildTransactionEdge({
      id: `e-${selectedNode.id}-${id}`,
      source: selectedNode.id,
      target: id,
      amount,
      layoutDir,
      heavy,
      edgeSettings,
    });

    const ns = [...nodes, newNode];
    const es = [...edges, newEdge];

    const finalNodes =
      autoArrangeOnAdd && !heavy ? applyLayout(ns, es) : syncGlobals(ns);

    setNodes(finalNodes);
    setEdges(es);
    fitToView();
    pushHistory(finalNodes, es);
    if (physicsEnabled) {
      startPhysics(finalNodes, es);
    }
    resetAddForm();
    showAddToast("Child added");
  }, [
    applyLayout,
    autoArrangeOnAdd,
    edgeSettings,
    edges,
    form,
    heavy,
    layoutDir,
    nodes,
    resetAddForm,
    selectedNode,
    pushHistory,
    physicsEnabled,
    showAddToast,
    startPhysics,
    syncGlobals,
  ]);

  const arrangeAllTreesAndFit = useCallback(() => {
    const arranged = applyLayout(nodesRef.current, edgesRef.current);
    setNodes(arranged);
    fitToView();
    pushHistory(arranged, edgesRef.current);
    if (physicsEnabled) {
      startPhysics(arranged, edgesRef.current);
    }
  }, [applyLayout, fitToView, physicsEnabled, pushHistory, startPhysics]);

  const manualAutoArrange = useCallback(() => {
    arrangeAllTreesAndFit();
  }, [arrangeAllTreesAndFit]);

  const handleFitViewAction = useCallback(() => {
    if (autoArrangeOnAdd && nodesRef.current.length) {
      arrangeAllTreesAndFit();
      return;
    }
    fitToView();
  }, [arrangeAllTreesAndFit, autoArrangeOnAdd, fitToView]);

  const deleteNode = useCallback(
    (node) => {
      if (!node) return;
      if (!effectiveCanDelete) {
        alert("Permission denied");
        return;
      }
      const confirmMsg = "Delete selected node and all its children?";
      if (!window.confirm(confirmMsg)) return;

      const toDelete = new Set([node.id]);
      let changed = true;
      while (changed) {
        changed = false;
        edgesRef.current.forEach((e) => {
          if (toDelete.has(e.source) && !toDelete.has(e.target)) {
            toDelete.add(e.target);
            changed = true;
          }
        });
      }

      const nextNodes = nodesRef.current.filter((n) => !toDelete.has(n.id));
      const nextEdges = edgesRef.current.filter(
        (e) => !toDelete.has(e.source) && !toDelete.has(e.target)
      );
      setNodes(nextNodes);
      setEdges(nextEdges);
      setSelectedNode(null);
      fitToView();
      pushHistory(nextNodes, nextEdges);
      if (physicsEnabled) {
        startPhysics(nextNodes, nextEdges);
      }
    },
    [effectiveCanDelete, fitToView, pushHistory, physicsEnabled, startPhysics]
  );

  const deleteSelectedNode = useCallback(() => deleteNode(selectedNode), [deleteNode, selectedNode]);

  const addChildFor = useCallback(
    (parentNode) => {
      if (!parentNode) return alert("Select a parent node first.");

      const id = `tx-${Date.now()}`;
      const bankColor = getBankColor(form.bank);
      const amount = safeNum(form.amount);

      const newNode = {
        id,
        type: "transaction",
        position:
          autoArrangeOnAdd && !heavy
            ? { x: parentNode.position.x + 40, y: parentNode.position.y + 40 }
            : getChildNodePosition(parentNode, edgesRef.current, layoutDir),
        style: { width: 230, height: 140, transition: "transform 140ms ease-out" },
        data: {
          ...form,
          amount: amount ? String(amount) : "",
          bankColor,
          layoutDir,
          readOnly,
          shape: form.shape || "rounded",
        },
      };

      const newEdge = buildTransactionEdge({
        id: `e-${parentNode.id}-${id}`,
        source: parentNode.id,
        target: id,
        amount,
        layoutDir,
        heavy,
        edgeSettings,
      });

      const ns = [...nodesRef.current, newNode];
      const es = [...edgesRef.current, newEdge];
      const finalNodes =
        autoArrangeOnAdd && !heavy ? applyLayout(ns, es) : syncGlobals(ns);

      setNodes(finalNodes);
      setEdges(es);
      fitToView();
      pushHistory(finalNodes, es);
      if (physicsEnabled) {
        startPhysics(finalNodes, es);
      }
      resetAddForm();
      showAddToast("Child added");
    },
    [applyLayout, autoArrangeOnAdd, edgeSettings, form, heavy, layoutDir, pushHistory, readOnly, resetAddForm, showAddToast, syncGlobals, fitToView, physicsEnabled, startPhysics]
  );

  const duplicateNode = useCallback(
    (node) => {
      if (!node) return;
      const parentEdge = edgesRef.current.find((e) => e.target === node.id);
      const parentId = parentEdge?.source || node.id;
      const id = `tx-${Date.now()}`;
      const amount = safeNum(node.data?.amount || node.data?.amount === 0 ? node.data.amount : form.amount);
      const bankColor = node.data?.bankColor || getBankColor(node.data?.bank);

      const newNode = {
        ...node,
        id,
        position: { x: node.position.x + 50, y: node.position.y + 50 },
        style: { ...(node.style || {}), transition: "transform 140ms ease-out" },
        data: {
          ...(node.data || {}),
          bankColor,
          layoutDir,
          readOnly,
          shape: node.data?.shape || form.shape || "rounded",
        },
      };

      const newEdge = buildTransactionEdge({
        id: `e-${parentId}-${id}`,
        source: parentId,
        target: id,
        amount,
        layoutDir,
        heavy,
        edgeSettings,
      });

      const ns = [...nodesRef.current, newNode];
      const es = [...edgesRef.current, newEdge];
      const finalNodes =
        autoArrangeOnAdd && !heavy ? applyLayout(ns, es) : syncGlobals(ns);

      setNodes(finalNodes);
      setEdges(es);
      fitToView();
      pushHistory(finalNodes, es);
      if (physicsEnabled) {
        startPhysics(finalNodes, es);
      }
    },
    [applyLayout, autoArrangeOnAdd, edgeSettings, fitToView, form.amount, heavy, layoutDir, pushHistory, readOnly, syncGlobals, physicsEnabled, startPhysics]
  );

  const handleNodeDragStart = useCallback((_, node) => {
    if (!physicsEnabled) return;
    const simNode = simulationRef.current?.nodes()?.find((n) => n.id === node.id);
    if (simNode) {
      simNode.fx = node.position.x;
      simNode.fy = node.position.y;
    }
  }, [physicsEnabled]);

  const handleNodeDrag = useCallback((_, node) => {
    const last = lastDragPosRef.current[node.id] || node.position;
    const dx = node.position.x - last.x;
    const dy = node.position.y - last.y;
    lastDragPosRef.current[node.id] = node.position;
    if (Math.abs(dx) + Math.abs(dy) < 0.1) return;

    if (physicsEnabled) {
      const simNode = simulationRef.current?.nodes()?.find((n) => n.id === node.id);
      if (simNode) {
        simNode.fx = node.position.x;
        simNode.fy = node.position.y;
      }
    }

    const neighbors = new Set();
    edgesRef.current.forEach((e) => {
      if (e.source === node.id) neighbors.add(e.target);
      if (e.target === node.id) neighbors.add(e.source);
    });
    if (!neighbors.size) return;

    setNodes((prev) =>
      prev.map((n) => {
        if (!neighbors.has(n.id)) return n;
        return {
          ...n,
          position: {
            x: n.position.x + dx * 0.08,
            y: n.position.y + dy * 0.08,
          },
        };
      })
    );
  }, [physicsEnabled]);

  const handleNodeDragStop = useCallback((_, node) => {
    delete lastDragPosRef.current[node.id];
    if (physicsEnabled) {
      const simNode = simulationRef.current?.nodes()?.find((n) => n.id === node.id);
      if (simNode) {
        simNode.fx = null;
        simNode.fy = null;
      }
      simulationRef.current?.alpha(0.6).restart();
    }
    pushHistory(nodesRef.current, edgesRef.current);
  }, [physicsEnabled, pushHistory]);

  const changeLayoutDir = useCallback(
    (dir) => {
      setLayoutDir(dir);
      const nextEdges = normalizeEdgesForLayout(
        edgesRef.current,
        dir,
        heavy,
        edgeSettings,
        true
      );
      setEdges(nextEdges);
      // re-layout current graph in chosen direction
      setNodes((prev) => {
        const arranged = layoutAllTrees(syncGlobals(prev, dir), nextEdges, dir);
        pushHistory(arranged, nextEdges);
        if (physicsEnabled) {
          startPhysics(arranged, nextEdges);
        }
        return arranged;
      });
      fitToView();
    },
    [edgeSettings, fitToView, heavy, physicsEnabled, pushHistory, startPhysics, syncGlobals]
  );

  const handleNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedCount = nodesRef.current.reduce(
      (count, currentNode) => count + (currentNode.selected ? 1 : 0),
      0
    );
    if (selectedCount === 0) {
      setNodes((prev) =>
        prev.map((currentNode) => {
          const shouldSelect = currentNode.id === node.id;
          if ((currentNode.selected || false) === shouldSelect) return currentNode;
          return {
            ...currentNode,
            selected: shouldSelect,
          };
        })
      );
    }
    setSelectedNode(node);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      node,
    });
  }, []);

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditingNodeId(null);
  }, []);

  const openEditDialogForNode = useCallback(
    (node) => {
      if (!effectiveCanEdit) {
        alert("Permission denied");
        return;
      }
      if (!node) return;
      const selectedCount = nodesRef.current.reduce(
        (count, currentNode) => count + (currentNode.selected ? 1 : 0),
        0
      );
      if (selectedCount > 1) return;

      const latestNode = nodesRef.current.find((n) => n.id === node.id) || node;
      setSelectedNode(latestNode);
      setEditingNodeId(latestNode.id);
      setEditForm(formFromNode(latestNode));
      setEditDialogOpen(true);
    },
    [effectiveCanEdit]
  );

  const handleNodeDoubleClick = useCallback(
    (event, node) => {
      event.preventDefault();
      const selectedNodes = nodesRef.current.filter((n) => n.selected);
      const isSingleSelectionOnNode =
        (selectedNodes.length === 1 && selectedNodes[0].id === node.id) ||
        (selectedNodes.length === 0 && selectedNode?.id === node.id);
      if (!isSingleSelectionOnNode) return;
      openEditDialogForNode(node);
    },
    [openEditDialogForNode, selectedNode]
  );

  const handleEditFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleEditSubmit = useCallback(() => {
    if (!editingNodeId) return;

    const currentNode = nodesRef.current.find((node) => node.id === editingNodeId);
    if (!currentNode) {
      closeEditDialog();
      return;
    }

    const amount = safeNum(editForm.amount);
    const nextNodes = nodesRef.current.map((node) =>
      node.id === editingNodeId
        ? {
            ...node,
            data: {
              ...(node.data || {}),
              ...editForm,
              amount: amount ? String(amount) : "",
              bankColor: getBankColor(editForm.bank),
              layoutDir,
              readOnly,
              shape: editForm.shape || "rounded",
            },
          }
        : node
    );

    const updatedSelectedNode =
      nextNodes.find((node) => node.id === editingNodeId) || null;
    setNodes(nextNodes);
    setSelectedNode(updatedSelectedNode);
    pushHistory(nextNodes, edgesRef.current);
    closeEditDialog();
  }, [closeEditDialog, editForm, editingNodeId, layoutDir, pushHistory, readOnly]);

  const handleContextAddChild = () => {
    if (contextMenu?.node) {
      addChildFor(contextMenu.node);
    }
    closeContextMenu();
  };

  const handleContextDuplicate = () => {
    if (contextMenu?.node) {
      duplicateNode(contextMenu.node);
    }
    closeContextMenu();
  };

  const handleContextEdit = () => {
    if (contextMenu?.node) {
      openEditDialogForNode(contextMenu.node);
    }
    closeContextMenu();
  };

  const handleContextDelete = () => {
    if (!effectiveCanDelete) {
      alert("Permission denied");
      return;
    }
    if (contextMenu?.node) {
      deleteNode(contextMenu.node);
    }
    closeContextMenu();
  };

  const canEditContextNode = useMemo(() => {
    if (!effectiveCanEdit || !contextMenu?.node) return false;
    const selectedNodes = nodes.filter((node) => node.selected);
    if (selectedNodes.length > 1) return false;
    if (selectedNodes.length === 1) {
      return selectedNodes[0].id === contextMenu.node.id;
    }
    return selectedNode?.id === contextMenu.node.id;
  }, [contextMenu, effectiveCanEdit, nodes, selectedNode]);

  useEffect(() => {
    if (!editDialogOpen || !editingNodeId) return;
    const exists = nodes.some((node) => node.id === editingNodeId);
    if (!exists) {
      setEditDialogOpen(false);
      setEditingNodeId(null);
    }
  }, [editDialogOpen, editingNodeId, nodes]);

  /* ---------------- Export ---------------- */

  const exportPNG = useCallback(async () => {
    const el = flowWrapRef.current?.querySelector(".react-flow");
    if (!el) return;
    const dataUrl = await toPng(el, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
    });
    saveAs(dataUrl, `${projectName || "transaction-trail"}.png`);
  }, [projectName]);

  const exportJSON = useCallback(() => {
    const payload = {
      meta: {
        projectName: projectName || "",
        layoutDir,
        exportedAt: new Date().toISOString(),
        version: "v2",
      },
      nodes,
      edges,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    saveAs(blob, `${projectName || "case"}.json`);
  }, [edges, layoutDir, nodes, projectName]);

  /* ---------------- Memo for perf ---------------- */
  const memoNodes = useMemo(() => nodes, [nodes]);
  const memoEdges = useMemo(() => edges, [edges]);

  /* =========================================================
     Loader Screen (initially visible)
  ========================================================== */
  if (showLoader) {
    const hasLast = projectList.length > 0;

    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Financial Transaction Trail
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {visitorMode
                ? "Load an existing local project, import JSON, or start a new one. Work is saved in browser session storage."
                : "Load an existing project, import JSON, or start a new one. Work is saved automatically in PostgreSQL."}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Resume</Typography>
              <Button variant="contained" onClick={resumeLast} disabled={!hasLast}>
                Resume Last Project
              </Button>
              {!hasLast && (
                <Typography variant="caption" color="text.secondary">
                  No last project found.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">Start New Project</Typography>
              <TextField
                label="Project name"
                placeholder="Project name (e.g., Enquiry 1385/24 - 28Jul)"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Layout</InputLabel>
                  <Select label="Layout" value={layoutDir} onChange={(e) => setLayoutDir(e.target.value)}>
                    <MenuItem value="LR">Horizontal</MenuItem>
                    <MenuItem value="TB">Vertical</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={startNew} disabled={!effectiveCanEdit}>
                  Create & Start
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">
                {visitorMode ? "Load Saved Projects" : "Load from Database"}
              </Typography>
              {projectList.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No saved projects found.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {projectList.map((p) => (
                    <Stack key={p.id} direction="row" spacing={1} flexWrap="wrap">
                      <Button variant="outlined" onClick={() => openProject(p)}>
                        {p.name}
                      </Button>
                      <Button variant="outlined" color="error" onClick={() => removeProject(p)} disabled={!effectiveCanDelete}>
                        Delete
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle1">Import Project JSON</Typography>
              <Button variant="outlined" component="label" disabled={!effectiveCanEdit}>
                Choose JSON File
                <input
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) importJSON(f);
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {visitorMode
                  ? "Import loads nodes/edges and keeps them in browser session storage."
                  : "Import loads nodes/edges and saves it into PostgreSQL automatically (resume later)."}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    );
  }

  /* =========================================================
     Main UI
  ========================================================== */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: 2,
        height: "100%",
        minHeight: { xs: 860, lg: "calc(100vh - 140px)" },
        width: "100%",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: { xs: "100%", lg: 360 },
          flexShrink: 0,
          p: 2,
          maxHeight: { lg: "100%" },
          overflow: "auto",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Project
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {projectName || "—"}
              </Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={() => setShowLoader(true)}>
              Projects Folder
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Nodes: {nodes.length} | Edges: {edges.length} {heavy ? " | Large graph" : ""}
          </Typography>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Transaction Entry
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
              Selected Parent: <strong>{selectedNode?.data?.nodeLabel || "—"}</strong>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={deleteSelectedNode}
                disabled={!selectedNode || !effectiveCanDelete}
                fullWidth
              >
                Delete Selected
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              <TextField
                label="Beneficiary Name"
                name="name"
                value={form.name}
                onChange={handleForm}
                size="small"
                fullWidth
              />
              <TextField
                label="Account Number"
                name="account"
                value={form.account}
                onChange={handleForm}
                size="small"
                fullWidth
              />
              <TextField
                label="Bank Name"
                name="bank"
                value={form.bank}
                onChange={handleForm}
                size="small"
                fullWidth
              />
              <TextField
                label="Amount (PKR)"
                name="amount"
                value={form.amount}
                onChange={handleForm}
                size="small"
                fullWidth
              />
              <TextField
                label="Date"
                type="date"
                name="date"
                value={form.date}
                onChange={handleForm}
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Time"
                type="time"
                name="time"
                value={form.time}
                onChange={handleForm}
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                select
                label="Channel"
                name="channel"
                value={form.channel}
                onChange={handleForm}
                size="small"
                fullWidth
              >
                <MenuItem value="IBFT">IBFT</MenuItem>
                <MenuItem value="ATM">ATM</MenuItem>
                <MenuItem value="Wallet">Wallet</MenuItem>
                <MenuItem value="Cash-out agent">Cash-out agent</MenuItem>
              </TextField>
              <TextField
                select
                label="Instrument"
                name="instrument"
                value={form.instrument}
                onChange={handleForm}
                size="small"
                fullWidth
              >
                <MenuItem value="Bank">Bank</MenuItem>
                <MenuItem value="JazzCash">JazzCash</MenuItem>
                <MenuItem value="Easypaisa">Easypaisa</MenuItem>
              </TextField>
              <TextField
                select
                label="Shape"
                name="shape"
                value={form.shape}
                onChange={handleForm}
                size="small"
                fullWidth
              >
                <MenuItem value="rounded">Rounded</MenuItem>
                <MenuItem value="sharp">Sharp</MenuItem>
                <MenuItem value="pill">Pill</MenuItem>
                <MenuItem value="circle">Circle</MenuItem>
              </TextField>
              <TextField
                select
                label="Arrow Type"
                value={edgeSettings.type}
                onChange={(e) => handleEdgeSettingChange("type", e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="auto">Auto (Layout Based)</MenuItem>
                <MenuItem value="bezier">Bezier</MenuItem>
                <MenuItem value="smoothstep">Smooth Step</MenuItem>
                <MenuItem value="step">Step</MenuItem>
                <MenuItem value="straight">Straight</MenuItem>
              </TextField>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Arrow Width"
                  type="number"
                  value={edgeSettings.lineWidth}
                  onChange={(e) =>
                    handleEdgeSettingChange("lineWidth", Number(e.target.value) || 1)
                  }
                  size="small"
                  inputProps={{ min: 1, max: 12, step: 0.2 }}
                  fullWidth
                />
                <TextField
                  label="Head Size"
                  type="number"
                  value={edgeSettings.headSize}
                  onChange={(e) =>
                    handleEdgeSettingChange("headSize", Number(e.target.value) || 10)
                  }
                  size="small"
                  inputProps={{ min: 10, max: 40, step: 1 }}
                  fullWidth
                />
              </Stack>
              {form.channel === "Cash-out agent" && (
                <>
                  <TextField
                    label="Agent shop name"
                    name="agentShop"
                    value={form.agentShop}
                    onChange={handleForm}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Agent city"
                    name="agentCity"
                    value={form.agentCity}
                    onChange={handleForm}
                    size="small"
                    fullWidth
                  />
                </>
              )}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={addRoot} fullWidth>
                  Add Root
                </Button>
                <Button variant="contained" onClick={addChild} fullWidth>
                  Add Child
                </Button>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={undo} disabled={historyIndex <= 0} fullWidth>
                  Undo
                </Button>
                <Button
                  variant="outlined"
                  onClick={redo}
                  disabled={historyIndex >= historyLength - 1}
                  fullWidth
                >
                  Redo
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Arrow type, line width, and head size are saved in browser storage and reused next time.
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Diagram Layout
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                select
                label="Direction"
                value={layoutDir}
                onChange={(e) => changeLayoutDir(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="LR">Horizontal (Left → Right)</MenuItem>
                <MenuItem value="TB">Vertical (Top → Bottom)</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoArrangeOnAdd}
                    onChange={(e) => setAutoArrangeOnAdd(e.target.checked)}
                    disabled={heavy}
                  />
                }
                label="Auto-arrange on add"
              />
              {heavy && (
                <Typography variant="caption" color="warning.main">
                  Large graph detected: disable auto-arrange-on-add and use manual arrange.
                </Typography>
              )}
              <Button variant="outlined" onClick={manualAutoArrange}>
                Manual Auto Arrange
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Export & Storage
            </Typography>
            <Stack spacing={1.5}>
              <Button variant="outlined" onClick={exportPNG}>
                Export PNG
              </Button>
              <Button variant="outlined" onClick={exportJSON}>
                Export JSON
              </Button>
              <Button variant="outlined" component="label">
                Import JSON
                <input
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) importJSON(f);
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {visitorMode
                  ? "Auto-save is ON. Your work is stored in this browser session."
                  : "Auto-save is ON. Your work is continuously saved in PostgreSQL under this project name."}
              </Typography>
              {visitorMode && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    clearVisitorData(VISITOR_PROJECTS_KEY);
                    setProjectList([]);
                    setProjectId(null);
                    setProjectName("");
                    setNodes([]);
                    setEdges([]);
                    setSelectedNode(null);
                    setShowLoader(true);
                  }}
                >
                  Clear Local Data
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Box
        ref={flowWrapRef}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          height: { xs: 520, lg: "calc(100vh - 220px)" },
          minHeight: 520,
          width: "100%",
        }}
      >
        <Paper variant="outlined" sx={{ height: "100%", overflow: "hidden", bgcolor: "background.paper" }}>
          <ReactFlow
            nodes={memoNodes}
            edges={memoEdges}
            nodeTypes={nodeTypes}
            onInit={(instance) => {
              reactFlowInstanceRef.current = instance;
              instance.fitView({ padding: 0.2 });
            }}
            onNodeClick={(_, n) => setSelectedNode(n)}
            onNodeContextMenu={handleNodeContextMenu}
            onNodeDoubleClick={handleNodeDoubleClick}
            onPaneClick={() => setSelectedNode(null)}
            onNodesChange={onNodesChange}
            onNodeDragStart={handleNodeDragStart}
            onNodeDrag={handleNodeDrag}
            onNodeDragStop={handleNodeDragStop}
            fitView
            onlyRenderVisibleElements
            panOnScroll={!heavy}
            zoomOnScroll={!heavy}
            nodesDraggable
            elementsSelectable
            style={{ width: "100%", height: "100%", minHeight: 520 }}
          >
            <Background />
            <Controls onFitView={handleFitViewAction} />
          </ReactFlow>
          <Menu
            open={contextMenu !== null}
            onClose={closeContextMenu}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
          >
            <MenuItem onClick={handleContextAddChild}>Add Child</MenuItem>
            <MenuItem onClick={handleContextDuplicate}>Duplicate</MenuItem>
            <MenuItem onClick={handleContextEdit} disabled={!canEditContextNode}>
              Edit
            </MenuItem>
            <MenuItem onClick={handleContextDelete} sx={{ color: "error.main" }} disabled={!effectiveCanDelete}>
              Delete
            </MenuItem>
          </Menu>
          <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
            <DialogTitle>Edit Transaction Node</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <TextField
                  label="Beneficiary Name"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Account Number"
                  name="account"
                  value={editForm.account}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Bank Name"
                  name="bank"
                  value={editForm.bank}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Amount (PKR)"
                  name="amount"
                  value={editForm.amount}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Date"
                  type="date"
                  name="date"
                  value={editForm.date}
                  onChange={handleEditFormChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Time"
                  type="time"
                  name="time"
                  value={editForm.time}
                  onChange={handleEditFormChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  select
                  label="Channel"
                  name="channel"
                  value={editForm.channel}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="IBFT">IBFT</MenuItem>
                  <MenuItem value="ATM">ATM</MenuItem>
                  <MenuItem value="Wallet">Wallet</MenuItem>
                  <MenuItem value="Cash-out agent">Cash-out agent</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Instrument"
                  name="instrument"
                  value={editForm.instrument}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="Bank">Bank</MenuItem>
                  <MenuItem value="JazzCash">JazzCash</MenuItem>
                  <MenuItem value="Easypaisa">Easypaisa</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Shape"
                  name="shape"
                  value={editForm.shape}
                  onChange={handleEditFormChange}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="rounded">Rounded</MenuItem>
                  <MenuItem value="sharp">Sharp</MenuItem>
                  <MenuItem value="pill">Pill</MenuItem>
                  <MenuItem value="circle">Circle</MenuItem>
                </TextField>
                {editForm.channel === "Cash-out agent" && (
                  <>
                    <TextField
                      label="Agent shop name"
                      name="agentShop"
                      value={editForm.agentShop}
                      onChange={handleEditFormChange}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Agent city"
                      name="agentCity"
                      value={editForm.agentCity}
                      onChange={handleEditFormChange}
                      size="small"
                      fullWidth
                    />
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleEditSubmit}
                disabled={!editingNodeId || !effectiveCanEdit}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={addToast.open}
            autoHideDuration={2000}
            onClose={closeAddToast}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            {/* Auto-dismiss success snippet for add actions */}
            <Alert
              onClose={closeAddToast}
              severity="success"
              variant="filled"
              sx={{ width: "100%" }}
            >
              {addToast.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
}
